import { useCallback, useContext, useState } from 'react';
import { useMutation } from '@apollo/client';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import CheckoutContext from '../lib/CheckoutContext';
import { useCartPricing } from '../contexts/CartPricingContext';
import { useLocale } from '../contexts/LocaleContext';
import useTenancy from './useTenancy';
import useCheckoutPaths from './useCheckoutPaths';
import { signup } from '../lib/authentication';
import { fetchRecaptchaToken } from '../lib/recaptcha';
import { parseStripeAddressName } from '../lib/nameParser';
import { toAddressInput } from '../lib/stripeAddress';
import { setGlobalVariablesForConversionTracking } from '../lib/analytics/analyticsUtils';
import { trackAgentioEmailCapture } from '../lib/analytics/agentio';
import { trackVibeLead } from '../lib/analytics/vibe';
import {
  getCustomerMessageFromApolloError,
  logInternalError,
  UserExistsSignUpError,
  UserFacingError,
} from '../lib/errors';
import { updateShippingAddressMutation } from '../graphql-operations';
import { sessionActions } from '../reducers/session';
import ThankYouLocationState from '../models/ThankYouLocationState';
import * as events from '../lib/analytics/events';
import * as types from '../types';
import type { StripeAddressValue } from '../components/StripeCheckout';

export interface PayPalSubmission {
  paypalTokenId: string;
  email: string;
  address: StripeAddressValue;
  shippingCode: types.ShippingCode;
  giftRecipient?: types.gqlTypes.GiftRecipientInput;
  /** a ship-to-recipient gift: the account and billing are the buyer's, the shipping address the recipient's */
  billingAddress?: StripeAddressValue;
}

interface ExistingUserLogin {
  email: string;
  submission: PayPalSubmission;
}

/**
 * Handles the full paypal purchase flow for inline checkout: signup (if new user) ->
 * save address + paypal billing token -> execute purchase -> navigate to thank-you.
 */
export default function usePayPalInlineCheckout() {
  const { t } = useTranslation();
  const { cart, session, checkoutActions, executePurchaseMutation } = useContext(CheckoutContext);
  const cartPricing = useCartPricing();
  const dispatch = useDispatch();
  const history = useHistory<ThankYouLocationState>();
  const checkoutPaths = useCheckoutPaths();
  const tenancy = useTenancy();
  const { currency } = useLocale();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string>();
  // the paypal email already has a fi account: the page asks the shopper to sign in, then the same
  // submission is finished as that user, as the card flow does
  const [existingUserLogin, setExistingUserLogin] = useState<ExistingUserLogin | null>(null);

  const [updateShipping] = useMutation<
    types.gqlTypes.updateShippingAddress,
    types.gqlTypes.updateShippingAddressVariables
  >(updateShippingAddressMutation);

  const purchase = useCallback(
    async (submission: PayPalSubmission, signedIn: boolean) => {
      setSubmitting(true);
      setError(undefined);

      try {
        const { paypalTokenId, email, address, shippingCode } = submission;
        // on a ship-to-recipient gift the form address is the recipient's: the account and billing take
        // the buyer's details, the order takes the recipient's through giftShippingAddress
        const { firstName, lastName } = parseStripeAddressName(submission.billingAddress ?? address);
        const customerName = parseStripeAddressName(address);
        const shippingAddress: types.gqlTypes.AddressInput = {
          line1: address.address.line1,
          line2: address.address.line2 ?? '',
          city: address.address.city,
          state: address.address.state,
          zip: address.address.postal_code,
          country: address.address.country,
          phone: address.phone,
        };
        const billingAddress = submission.billingAddress ? toAddressInput(submission.billingAddress) : shippingAddress;

        // new user: create fi + recurly account first
        if (!signedIn) {
          const signupRecaptcha = await fetchRecaptchaToken('signup');
          const newSession = await signup(email, null, firstName, lastName, tenancy, signupRecaptcha, true);
          dispatch(sessionActions.createSession({ session: newSession }));
        }

        // save address + paypal billing token (works for both new and existing users)
        const billingRecaptcha = await fetchRecaptchaToken('payment_add');
        await updateShipping({
          variables: {
            firstName,
            lastName,
            address: billingAddress,
            billingInfo: { token: paypalTokenId },
            recaptchaToken: billingRecaptcha,
          },
        });

        // dispatch checkout state for analytics
        dispatch(checkoutActions.setCustomerName(customerName));
        dispatch(checkoutActions.setShippingAddress({ shippingAddress }));
        dispatch(checkoutActions.setShippingCode({ shippingCode }));
        dispatch(
          checkoutActions.setBillingInfo({
            billingInfo: {
              firstName,
              lastName,
              address: billingAddress,
              paymentInfo: { __typename: 'PayPalInfo' as const },
            },
          }),
        );

        events.shipping.continue(cart.cartItems, currency);
        events.payment.continue(cart.cartItems, currency);
        trackAgentioEmailCapture();
        trackVibeLead();
        events.checkoutFunnel.infoSubmitted('stripe');

        // execute purchase
        const checkoutState: types.CheckoutState = {
          shippingAddress,
          shippingCode,
          customerName,
          billingInfo: {
            firstName,
            lastName,
            address: billingAddress,
            paymentInfo: { __typename: 'PayPalInfo' },
          },
          giftRecipient: submission.giftRecipient,
        };

        const result = await executePurchaseMutation(cart, checkoutState);
        const { chargeInvoice, giftCardInvoices, isGWP, isReturningCustomer } = result;
        const invoiceNumber = chargeInvoice?.number ?? giftCardInvoices?.[0]?.number;
        const cartItemsArray = Object.values(cart.cartItems);

        setGlobalVariablesForConversionTracking(invoiceNumber, cartItemsArray, cartPricing, email);

        try {
          events.confirmation.finalConfirm(cartItemsArray, cartPricing);
          events.checkoutFunnel.purchaseSucceeded('stripe');
          events.orderCompleted(cart, cartPricing, invoiceNumber, undefined, undefined, {
            isReturningCustomer,
            isGWP,
            currency,
            paymentType: 'paypal',
          });
        } catch {
          // analytics should never block navigation
        }

        history.replace(checkoutPaths.ThankYou, {
          orderId: invoiceNumber,
          orderedCart: cart,
          orderedCartPricing: cartPricing,
          giftRecipient: submission.giftRecipient,
        });
      } catch (err) {
        if (err instanceof UserExistsSignUpError) {
          events.payment.continueError(err.message);
          setExistingUserLogin({ email: submission.email, submission });
          return;
        }
        if (err instanceof UserFacingError) {
          setError(err.message);
          return;
        }
        const customerMessage = getCustomerMessageFromApolloError(err);
        const message = customerMessage ?? t('something_went_wrong');
        if (!customerMessage) {
          logInternalError(err, {
            fingerprint: ['{{ default }}', err instanceof Error ? err.message : 'unknown'],
            tags: { checkoutStep: 'paypalInlineCheckout', paymentMethod: 'paypal' },
          });
        }
        setError(message);
      } finally {
        setSubmitting(false);
      }
    },
    [
      cart,
      cartPricing,
      currency,
      tenancy,
      dispatch,
      checkoutActions,
      executePurchaseMutation,
      updateShipping,
      history,
      checkoutPaths,
      t,
    ],
  );

  const handlePayPalToken = useCallback(
    (submission: PayPalSubmission) => purchase(submission, !!session),
    [purchase, session],
  );

  const onExistingUserLogin = useCallback(
    async (loggedInSession: types.Session) => {
      if (!existingUserLogin) {
        return;
      }
      setExistingUserLogin(null);
      dispatch(sessionActions.createSession({ session: loggedInSession }));
      await purchase(existingUserLogin.submission, true);
    },
    [existingUserLogin, dispatch, purchase],
  );

  return { handlePayPalToken, submitting, error, existingUserLogin, onExistingUserLogin };
}
